import React, { useState } from 'react';
import { ChartFilter } from '../ChartComponents';
import { ChartCommonProps, IconButtonWrapper } from '../Common';
import { SelectedItemProps } from '../MattersByStatusChart';
import {
    IconButton,
    Theme,
    createStyles,
    makeStyles,
} from '@material-ui/core';
import { ChartInfo } from '../../components/ChartInfo';
import MTooltip from '@material-ui/core/Tooltip';
import { VideoModal } from '../../components/VideoModal';
import VideoLibraryOutlinedIcon from '@material-ui/icons/VideoLibraryOutlined';
import GetAppOutlinedIcon from '@material-ui/icons/GetAppOutlined';
import AspectRatioIcon from '@material-ui/icons/AspectRatio';
import CloseIcon from '@material-ui/icons/Close';
import { useScreenshot, createFileName } from 'use-react-screenshot';
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';
import { ChartSkeleton } from '../../components/loading/ChartSkeleton';
import { AIIcon } from '../../components/icons/AIIcon';
import html2canvas from 'html2canvas';
import { AIChartDialog } from '../../components/Dialogs/AIChartDialog';
import FavoriteBorderOutlinedIcon from '@material-ui/icons/FavoriteBorderOutlined';
import { RV_CHART_CLASS, RV_CHART_COLORS } from '../chartTheme';
import { ChartHeading } from '../ChartHeading';
import { AnimatedNumber } from '../AnimatedNumber';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        rootLoader: {
            display: 'flex',
            flexFlow: 'column',
            maxWidth: '100%',
            maxHeight: '100%',
            height: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
        },
        root: {
            position: 'relative',
            paddingBottom: '10px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            maxWidth: '100%',
            maxHeight: '100%',
            height: '100%',
            textAlign: 'center',
            '@media print': {
                display: 'flex',
                flexFlow: 'column',
                alignItems: 'center',
                boxShadow: 'none !important',
            },
        },
        helpIcon: {
            position: 'absolute',
            left: '5px',
            top: '5px',
            '@media print': {
                display: 'none !important',
            },
        },
        learnIcon: {
            color: 'rgb(0, 120, 212)',
        },
        titleContainer: {
            display: 'flex',
            flexFlow: 'column',
            padding: '20px 15px 0 15px',
        },
        title: {
            fontSize: '24px',
            fontWeight: 600,
            fontFamily: 'inherit',
            marginBottom: 0,
        },
        barContainer: {
            padding: '15px 15px 15px 15px',
            minHeight: '300px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            gap: '10px',
            textAlign: 'left',
        },
        pointer: {
            cursor: 'pointer',
        },
        hero: {
            display: 'flex',
            alignItems: 'baseline',
            gap: 10,
            '& b': {
                fontSize: 36,
                lineHeight: 1,
                letterSpacing: -1.2,
                fontVariantNumeric: 'tabular-nums',
            },
            '& span': {
                color: RV_CHART_COLORS.muted,
                fontSize: 13,
            },
        },
        healthBar: {
            display: 'flex',
            height: 8,
            borderRadius: 999,
            overflow: 'hidden',
            backgroundColor: '#eef0f6',
            gap: 2,
            margin: '4px 0 0 0',
            '& i': {
                display: 'block',
                height: '100%',
                transition: 'width 0.7s cubic-bezier(0.2, 0.8, 0.2, 1)',
            },
        },
        legend: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px 14px',
            fontSize: 12,
            color: RV_CHART_COLORS.muted,
            '& i': {
                display: 'inline-block',
                width: 9,
                height: 9,
                borderRadius: 3,
                marginRight: 5,
            },
            '& b': {
                color: RV_CHART_COLORS.ink,
                fontVariantNumeric: 'tabular-nums',
            },
        },
        tiles: {
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
        },
        tile: {
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 10,
            padding: '10px 12px 9px 12px',
            background: 'linear-gradient(180deg, #fff 0%, #fafbff 100%)',
            transition: 'border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease',
            '&:hover': {
                borderColor: '#cfd6f7',
                boxShadow: '0 4px 14px rgba(16, 26, 51, 0.08)',
                transform: 'translateY(-1px)',
            },
            '@media (prefers-reduced-motion: reduce)': {
                transition: 'none',
                '&:hover': {
                    transform: 'none',
                },
            },
        },
        tileTop: {
            display: 'flex',
            alignItems: 'center',
            gap: 10,
        },
        tileDot: {
            width: 10,
            height: 10,
            borderRadius: '50%',
            flex: '0 0 auto',
        },
        tileName: {
            flex: 1,
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: '0.07em',
            textTransform: 'uppercase',
            color: RV_CHART_COLORS.muted,
        },
        tilePill: {
            fontSize: 11.5,
            fontWeight: 800,
            borderRadius: 999,
            padding: '2px 9px',
            backgroundColor: '#fee2e2',
            color: '#b91c1c',
            whiteSpace: 'nowrap',
        },
        tileNumber: {
            display: 'flex',
            alignItems: 'baseline',
            gap: 8,
            margin: '2px 0 0 0',
            '& b': {
                fontSize: 24,
                fontWeight: 800,
                letterSpacing: -0.6,
                fontVariantNumeric: 'tabular-nums',
            },
            '& span': {
                color: RV_CHART_COLORS.muted,
                fontSize: 12.5,
            },
        },
        tileHelp: {
            margin: '2px 0 0 0',
            fontSize: 12,
            color: RV_CHART_COLORS.faint,
        },
        splitBar: {
            display: 'flex',
            height: 6,
            borderRadius: 999,
            overflow: 'hidden',
            backgroundColor: '#eef0f6',
            margin: '8px 0 6px 0',
            gap: 2,
            '& i': {
                display: 'block',
                height: '100%',
                transition: 'flex-grow 0.7s cubic-bezier(0.2, 0.8, 0.2, 1)',
            },
        },
        tileLines: {
            display: 'flex',
            justifyContent: 'space-between',
            gap: 8,
            fontSize: 12,
            color: RV_CHART_COLORS.muted,
            '& b': {
                color: RV_CHART_COLORS.ink,
                fontVariantNumeric: 'tabular-nums',
            },
        },
        tileLineLate: {
            color: '#b91c1c',
            '& b': {
                color: '#b91c1c',
            },
        },
        people: {
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 6,
            marginTop: 8,
            paddingTop: 8,
            borderTop: `1px dashed ${theme.palette.divider}`,
        },
        peopleHeading: {
            width: '100%',
            fontSize: 10.5,
            fontWeight: 800,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: RV_CHART_COLORS.faint,
        },
        personChip: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '2px 9px 2px 3px',
            borderRadius: 999,
            backgroundColor: '#f4f6fb',
            fontSize: 12,
            whiteSpace: 'nowrap',
        },
        personInitials: {
            width: 20,
            height: 20,
            borderRadius: '50%',
            backgroundColor: '#eef1ff',
            color: '#5173ff',
            fontSize: 9.5,
            fontWeight: 800,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        personCount: {
            fontWeight: 800,
            fontVariantNumeric: 'tabular-nums',
        },
        quietRow: {
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '8px 12px',
            borderRadius: 10,
            backgroundColor: '#f7f8fc',
            fontSize: 12.5,
            color: RV_CHART_COLORS.muted,
            '& b': {
                fontSize: 18,
                color: RV_CHART_COLORS.ink,
                fontVariantNumeric: 'tabular-nums',
            },
        },
    }),
);

// A third colour for "follow up overdue" so the three problems stay distinguishable in the strip and the tiles.
const HEALTH_OVERDUE_COLOR = '#8b5cf6';

const getInitials = (name: string) =>
    name
        .split(' ')
        .filter((namePart) => namePart.length > 0)
        .slice(0, 2)
        .map((namePart) => namePart[0].toUpperCase())
        .join('');

interface CrmEnquiresDataHealthChartChartProps extends SelectedItemProps, ChartCommonProps {
    chartFilter?: ChartFilter;
    infoUrl: string | null;
}

export const CrmEnquiresDataHealthChart = (props: CrmEnquiresDataHealthChartChartProps) => {
    const classes = useStyles();

    const chartName = 'CRM Enquires Data Health Chart';

    // tslint:disable-next-line: no-any
    const entireChartRef = React.useRef<any>();

    const [isVideoModalOpen, setIsVideoModalOpen] = useState<boolean>(false);

    const [base64Data, setBase64Data] = useState<string | ArrayBuffer | null>(null);
    
    const [isAIDialogOpen, setIsAIDialogOpen] = useState<boolean>(false);

    const [isBlobGenerating, setIsBlobGenerating] = useState<boolean>(false);

    const takeScreenShot = useScreenshot({
        type: 'image/jpeg',
        quality: 1.0,
    })[1];

    const enquiryHealthQuery = useQuery<EnquiryHealthQueryData, EnquiryHealthQueryParams>(EnquiryHealthQuery, {
        variables: {
            entityType: props.chartFilter?.entityType,
            entityGuid: props.chartFilter?.entityGuid
        }
    });

    const onToggleVideo = () => {
        setIsVideoModalOpen(!isVideoModalOpen);
    };

    const onMaximize = () => {
        if (props.onMaximize) {
            props.onMaximize();
        }
    };

    const onCloseChart = () => {
        if (props.onCloseChart) {
            props.onCloseChart(`isEnquiryHealthChartOpen`);
        }
    };

    const onDownload =
        (name: string, extension: string = 'jpg') =>
        // tslint:disable-next-line: no-any
        (image: any) => {
            const a = document.createElement('a');
            a.href = image;
            a.download = createFileName(extension, name);
            a.click();
        };

    // tslint:disable-next-line: no-any
    const onDownloadScreenshot = (event: any) => {
        event.preventDefault();
        event.stopPropagation();
        setTimeout(() => {
            takeScreenShot(entireChartRef.current).then(onDownload(`CRM Enquires Data Health`));
            // tslint:disable-next-line: align
        }, 500);
    };

    const onAIGeneratedSuggestion = async (event: any) => {
    
        setIsBlobGenerating(true);

        event.preventDefault();
        event.stopPropagation();
        
        if (entireChartRef && entireChartRef.current) {
            const canvas = await html2canvas(entireChartRef.current);
            canvas.toBlob((blob) => {
                if (blob) {
                    const reader = new FileReader();
                    reader.readAsDataURL(blob);
                    reader.onloadend = async () => {
                        const tempBase64data = reader.result;
                        // console.log(tempBase64data); // This is your image blob string
                        setBase64Data(tempBase64data);
                        setIsAIDialogOpen(true);
                    };
                }
            });
        }

        setIsBlobGenerating(false);
    };

    const chartControlComponent = () => {
        return (
            <>
                <IconButtonWrapper aria-label="close">
                    <div className={`${classes.helpIcon} rv-chart-help`}>
                        <ChartInfo
                            description={props.description}
                            fontSize={props.showAllRecords ? 'large' : 'medium'}
                            // className={classes.helpIcon}
                        />
                        {props.infoUrl && (
                            <MTooltip title="Learn more">
                                <IconButton className={classes.learnIcon} aria-label="maximize" onClick={onToggleVideo} size={props.showAllRecords ? 'medium' : 'small'}>
                                    <VideoLibraryOutlinedIcon />
                                </IconButton>
                            </MTooltip>
                        )}
                    </div>
                    {props.isAIFeatureEnabled && (
                        <MTooltip title="AI Assistant">
                            <IconButton 
                                disabled={isBlobGenerating}
                                size={props.showAllRecords ? 'medium' : 'small'}
                                onClick={onAIGeneratedSuggestion}
                            >
                                <AIIcon 
                                    color="primary"
                                    fontSize={props.showAllRecords ? 'large' : 'medium'}
                                />
                            </IconButton>
                        </MTooltip>
                    )}
                    {(props.showAllRecords || props.showScreenshot) && (
                        <IconButton 
                            size={props.showAllRecords ? 'medium' : 'small'}
                            onClick={onDownloadScreenshot}
                        >
                            <GetAppOutlinedIcon 
                                color="primary" 
                                fontSize={props.showAllRecords ? 'large' : 'medium'}
                            />
                        </IconButton>
                    )}
                    {!props.showAllRecords && props.onMaximize && (
                        <MTooltip title="Expand chart">
                            <IconButton aria-label="maximize" onClick={onMaximize} size="small">
                                <AspectRatioIcon />
                            </IconButton>
                        </MTooltip>
                    )}
                    <IconButton aria-label="close" onClick={onCloseChart} size={props.showAllRecords ? 'medium' : 'small'}>
                        <CloseIcon fontSize={props.showAllRecords ? 'large' : 'medium'} />
                    </IconButton>
                </IconButtonWrapper>
            </>
        );
    };

    if (enquiryHealthQuery.loading) {
        return (
            <div className={`${classes.rootLoader} ${RV_CHART_CLASS}`}>
                {chartControlComponent()}
                <ChartSkeleton variant="bars" />
            </div>
        );
    }

    const { 
        open,
        followupOverdue,
        followupOverdue3Plus,
        followupOverdueAssignees,
        followupOverdueSub3,
        noFollowup,
        noFollowup3Plus,
        noFollowupAssignees,
        noFollowupSub3,
        pendingMatter,
        unassigned,
        unassigned5Plus,
        unassignedSub5
    } = enquiryHealthQuery.data!.statistics.lead.enquiryHealth;

    // The coloured strip under the total: each problem's share of the open enquiries (scaled to the larger of the two if they ever overlap).
    const healthSegments = [
        { label: 'Unassigned', count: unassigned, color: RV_CHART_COLORS.red },
        { label: 'Not followed up', count: noFollowup, color: RV_CHART_COLORS.amber },
        { label: 'Follow up overdue', count: followupOverdue, color: HEALTH_OVERDUE_COLOR },
        { label: 'Pending matter', count: pendingMatter, color: RV_CHART_COLORS.faint },
    ];
    const healthBarTotal = Math.max(open, healthSegments.reduce((sum, segment) => sum + segment.count, 0), 1);

    // Recent (green) versus older (red) enquiries, using the two counts exactly as the lines under it show them.
    const renderSplitBar = (recentCount: number, olderCount: number) => (
        <div className={classes.splitBar}>
            <i style={{ flexGrow: recentCount, backgroundColor: RV_CHART_COLORS.green }} />
            <i style={{ flexGrow: olderCount, backgroundColor: RV_CHART_COLORS.red }} />
        </div>
    );

    const renderAssignees = (heading: string, assignees: EnquiryAssignee[] | null) => {
        if (!assignees) {
            return null;
        }
        return (
            <div className={classes.people}>
                <span className={classes.peopleHeading}>{heading}</span>
                {assignees.map((assignee, index) => (
                    <span key={index} className={classes.personChip} title={assignee.name}>
                        <span className={classes.personInitials}>{getInitials(assignee.name)}</span>
                        {assignee.name.split(' ')[0]}
                        <span className={classes.personCount}>{assignee.count.toLocaleString()}</span>
                    </span>
                ))}
            </div>
        );
    };

    // One tile per problem: the count, how much of it is recent versus old, and who has the most.
    const renderTile = (
        color: string,
        name: string,
        count: number,
        description: string,
        helpText: string,
        olderPill: string,
        recentLabel: string,
        recentCount: number,
        olderLabel: string,
        olderCount: number,
        assigneesHeading?: string,
        assignees?: EnquiryAssignee[] | null,
    ) => (
        <div className={classes.tile}>
            <div className={classes.tileTop}>
                <span className={classes.tileDot} style={{ backgroundColor: color }} />
                <span className={classes.tileName}>{name}</span>
                {olderCount > 0 && count > 0 && (
                    <span className={classes.tilePill} title={`${olderCount.toLocaleString()} ${olderPill}`}>{`${Math.round((olderCount / count) * 100)}% ${olderPill}`}</span>
                )}
            </div>
            <div className={classes.tileNumber}>
                <b>{count.toLocaleString()}</b>
                <span>{description}</span>
            </div>
            <div className={classes.tileHelp}>{helpText}</div>
            {renderSplitBar(recentCount, olderCount)}
            <div className={classes.tileLines}>
                <span>
                    {`${recentLabel} `}
                    <b>{recentCount.toLocaleString()}</b>
                </span>
                {olderCount > 0 && (
                    <span className={classes.tileLineLate}>
                        {`${olderLabel} `}
                        <b>{olderCount.toLocaleString()}</b>
                    </span>
                )}
            </div>
            {assigneesHeading && renderAssignees(assigneesHeading, assignees || null)}
        </div>
    );

    return (
        <>
            {isAIDialogOpen && (
                <AIChartDialog 
                    fullWidth={true}
                    maxWidth="md"
                    open={isAIDialogOpen}
                    onClose={() => setIsAIDialogOpen(false)}
                    data={base64Data}
                    query={props.aiPrompt || 'Review the attached data and provide a summary assessment of the situation and anything that can be done to improve it'}
                    accessSubType={chartName}
                    layoutId={props.layoutId}
                />
            )}
            <div className={`${classes.root} ${RV_CHART_CLASS}`} ref={entireChartRef}>
                {isVideoModalOpen && props.infoUrl && <VideoModal open={isVideoModalOpen} onClose={onToggleVideo} url={props.infoUrl} />}
                {chartControlComponent()}
                <div className={classes.titleContainer}>
                    <ChartHeading icon={<FavoriteBorderOutlinedIcon />} title="CRM Enquiries Data Health" subtitle="Open enquiries needing attention" />
                </div>
                <div className={classes.barContainer}>
                    <div className={classes.hero}>
                        <b>
                            <AnimatedNumber value={open} format={(amount) => Math.round(amount).toLocaleString()} />
                        </b>
                        <span>open enquiries</span>
                    </div>
                    <div className={classes.healthBar} title={healthSegments.map((segment) => `${segment.label} ${segment.count.toLocaleString()}`).join(' · ')}>
                        {healthSegments.map((segment) => (
                            <i key={segment.label} style={{ width: `${(segment.count / healthBarTotal) * 100}%`, backgroundColor: segment.color }} />
                        ))}
                    </div>

                    <div className={classes.legend}>
                        {healthSegments.map((segment) => (
                            <span key={segment.label}>
                                <i style={{ backgroundColor: segment.color }} />
                                {segment.label}
                                {' '}
                                <b>{segment.count.toLocaleString()}</b>
                            </span>
                        ))}
                    </div>

                    <div className={classes.tiles}>
                        {renderTile(RV_CHART_COLORS.red, 'Unassigned', unassigned, 'enquiries', 'No one has been assigned to these enquiries yet.', 'over 5 days', 'Under 5 days', unassignedSub5, 'Over 5 days', unassigned5Plus)}
                        {renderTile(
                            RV_CHART_COLORS.amber, 'Assigned, not followed up', noFollowup, 'enquiries', 'Assigned to someone, but no follow up has been recorded yet.', 'over 3 days',
                            'Assigned in last 3 days', noFollowupSub3, 'Assigned over 3 days ago', noFollowup3Plus, 'Most not followed up', noFollowupAssignees,
                        )}
                        {renderTile(
                            HEALTH_OVERDUE_COLOR, 'Follow up overdue', followupOverdue, 'enquiries', 'A follow up was due and its date has now passed.', 'over 3 days',
                            'Due in last 3 days', followupOverdueSub3, 'Due over 3 days ago', followupOverdue3Plus, 'Most overdue', followupOverdueAssignees,
                        )}

                        <div className={classes.quietRow}>
                            <span className={classes.tileDot} style={{ backgroundColor: RV_CHART_COLORS.faint }} />
                            <b>{pendingMatter.toLocaleString()}</b>
                            <span>enquiries with a status of Pending Matter</span>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );    
};

type EnquiryHealthQueryParams = {
    entityType?: number;
    entityGuid?: string;
};

type EnquiryHealthQueryData = {
    statistics: Statistics;
};

type Statistics = {
    lead: Lead;
};

type Lead = {
    enquiryHealth: EnquiryHealth;
};

type EnquiryHealth = {
    open:                     number;
    pendingMatter:            number;
    followupOverdue:          number;
    followupOverdue3Plus:     number;
    followupOverdueSub3:      number;
    followupOverdueAssignees: EnquiryAssignee[] | null;
    noFollowup:               number;
    noFollowup3Plus:          number;
    noFollowupSub3:           number;
    noFollowupAssignees:      EnquiryAssignee[] | null;
    unassigned:               number;
    unassigned5Plus:          number;
    unassignedSub5:           number;
};

type EnquiryAssignee = {
    count: number;
    name: string;
};

export const EnquiryHealthQuery = gql`
    query EnquiryHealth($entityType: Int, $entityGuid: String) {
        statistics {
            lead {
                enquiryHealth (entityType: $entityType, entityGuid: $entityGuid) {
                    open
                    pendingMatter
                    followupOverdue
                    followupOverdue3Plus
                    followupOverdueSub3
                    followupOverdueAssignees {
                        count
                        name
                    }
                    noFollowup
                    noFollowup3Plus
                    noFollowupSub3
                    noFollowupAssignees {
                        count
                        name
                    }
                    unassigned
                    unassigned5Plus
                    unassignedSub5
                }
            }
        }
    }
`;
